import React, { useState } from 'react';
import { WarningComponent as WarningIcon } from 'ui/Icons';
import AnimateHeight from 'react-animate-height';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import classNames from 'classnames';

export interface IAnnouncementItem {
  uuid: string;
  title: string;
  description: string;
}

export interface IHotelAnnouncements {
  hotelUuid: string;
  hotelName: string;
  announcements: IAnnouncementItem[];
}

export interface IMultiHotelAnnouncementsDisplayRenderProps {
  hotelAnnouncements: IHotelAnnouncements[];
  className?: string;
  maxInitialDisplay?: number; // Default 3
  titleClassName?: string;
}

/**
 * Presentational component to display announcements grouped by multiple hotels.
 * This component does NOT fetch data - it receives hotel announcements as props.
 * Shows expandable announcements for each hotel.
 */
export const MultiHotelAnnouncementsDisplayRender: React.FC<IMultiHotelAnnouncementsDisplayRenderProps> = ({
  hotelAnnouncements,
  className,
  maxInitialDisplay = 3,
  titleClassName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate total number of announcements across all hotels
  const totalAnnouncements = hotelAnnouncements.reduce(
    (sum, hotel) => sum + hotel.announcements.length,
    0
  );

  if (hotelAnnouncements.length === 0 || totalAnnouncements === 0) {
    return null;
  }

  return (
    <div className={classNames('px-4 py-10px mt-25px bg-yellow-advert-light border border-yellow-advert', className)}>
      <div className="flex items-center justify-center">
        <WarningIcon />
        <p
          className={classNames(
            'text-brown-prime text-16px leading-21px font-pt-sans ml-10px my-0 flex-1 uppercase',
            titleClassName
          )}
        >
          Important Announcements ({totalAnnouncements})
        </p>
      </div>

      <div>
        {hotelAnnouncements.map(hotel => (
          <div key={hotel.hotelUuid} className="mb-4 last:mb-0">
            <h3 className="text-brown-prime text-16px font-bold m-2 font-pt-sans">
              {hotel.hotelName}
            </h3>
            <ul className="list-disc pl-5 m-0 p-0">
              {hotel.announcements.map(announcement => (
                <li key={announcement.uuid} className="m-2 last:mb-0">
                  <div className="font-bold text-black text-15px">{announcement.title}</div>
                  <AnimateHeight duration={300} height='auto' className="accordion-area">
                    <HtmlStringRenderer content={announcement.description} className="text-black text-14px mt-1" />
                  </AnimateHeight>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {totalAnnouncements > maxInitialDisplay && (
        <div className="text-brown-prime text-sm mt-10px cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Show less' : 'Show more'}
        </div>
      )}
    </div>
  );
};

